import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Input, Select, Textarea, Radio } from '../../components/Fields';
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Heart,
  Activity,
  FileText,
  Save,
  X,
  Plus,
  Trash2,
  Shield,
  Users,
  CreditCard,
} from 'lucide-react';
import {
  userRoles,
  genderOptions,
  maritalStatus,
  pregnancyStatus,
  bloodGroups,
  subscriptionDetails,
} from '../../ServicesConfig';
import {
  updateFormField,
  addMedicalRecord,
  removeMedicalRecord,
  addMedicineIntake,
  removeMedicineIntake,
  createCustomer,
  fetchAssociates,
  resetForm,
} from '../../redux/slices/customerSlice';

const AddCustomer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { formData, associates, isSaving, error, success } = useSelector(
    (state) => state.customer
  );

  const [activeTab, setActiveTab] = useState('personal');
  const [newMedicalRecord, setNewMedicalRecord] = useState({ condition: '', date: '', notes: '' });
  const [newMedicine, setNewMedicine] = useState({ name: '', dosage: '', frequency: '' });

  useEffect(() => {
    dispatch(fetchAssociates());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      alert('Customer added successfully!');
      dispatch(resetForm());
      navigate('/admin/users');
    }
  }, [success, dispatch, navigate]);

  const handleFieldChange = (field, value) => {
    dispatch(updateFormField({ field, value }));
  };

  const handleAddMedicalRecord = () => {
    if (newMedicalRecord.condition && newMedicalRecord.date) {
      dispatch(addMedicalRecord(newMedicalRecord));
      setNewMedicalRecord({ condition: '', date: '', notes: '' });
    }
  };

  const handleAddMedicine = () => {
    if (newMedicine.name && newMedicine.dosage) {
      dispatch(addMedicineIntake(newMedicine));
      setNewMedicine({ name: '', dosage: '', frequency: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.mobile) {
      alert('Please fill in required fields: Name and Mobile');
      return;
    }

    if (formData.email && formData.password !== formData.confirmPassword) {
      alert('Password and Confirm Password do not match');
      return;
    }

    dispatch(createCustomer(formData));
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'health', label: 'Health Records', icon: Activity },
    { id: 'family', label: 'Family & Reference', icon: Users },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'account', label: 'Account & Security', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Customer</h1>
              <p className="text-sm text-gray-600 mt-1">Create a comprehensive customer profile</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  dispatch(resetForm());
                  navigate(-1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save Customer'}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-max px-6 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <form onSubmit={handleSubmit}>
          {/* Personal Information Tab */}
          {activeTab === 'personal' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Enter full name"
                  required
                  icon="user"
                />

                <Input
                  label="Mobile Number"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => handleFieldChange('mobile', e.target.value)}
                  placeholder="Enter mobile number"
                  required
                  icon={Phone}
                />

                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder="Enter email address"
                  icon={Mail}
                  optional
                />

                <Input
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                  icon={Calendar}
                />

                <Input
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  readOnly
                  placeholder="Auto-calculated"
                />

                <Select
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  options={genderOptions}
                  placeholder="Select gender"
                />

                <Select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={(e) => handleFieldChange('role', e.target.value)}
                  options={userRoles}
                />

                {formData.role === 'customer' && (
                  <Select
                    label="Under Associate"
                    name="underAssociate"
                    value={formData.underAssociate}
                    onChange={(e) => handleFieldChange('underAssociate', e.target.value)}
                    options={associates.map((a) => ({ label: `${a.name} (${a.mobile})`, value: a.id }))}
                    placeholder="Select associate"
                    optional
                  />
                )}

                {formData.role === 'associate' && (
                  <Input
                    label="Associate ID"
                    name="associateId"
                    value={formData.associateId}
                    onChange={(e) => handleFieldChange('associateId', e.target.value)}
                    placeholder="Enter associate ID"
                  />
                )}

                {formData.role === 'preferred_customer' && (
                  <Input
                    label="Preferred Customer ID"
                    name="preferredCustomerId"
                    value={formData.preferredCustomerId}
                    onChange={(e) => handleFieldChange('preferredCustomerId', e.target.value)}
                    placeholder="Enter preferred customer ID"
                  />
                )}
              </div>
            </div>
          )}

          {/* Health Records Tab */}
          {activeTab === 'health' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Health & Medical Records
              </h2>

              {/* Basic Health Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Height (cm)"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={(e) => handleFieldChange('height', e.target.value)}
                  placeholder="Enter height"
                />

                <Select
                  label="Blood Group"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={(e) => handleFieldChange('bloodGroup', e.target.value)}
                  options={bloodGroups}
                  placeholder="Select blood group"
                />
              </div>

              {/* Medical Records */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Medical Records</h3>

                {/* Add Medical Record */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Input
                    label="Condition"
                    value={newMedicalRecord.condition}
                    onChange={(e) => setNewMedicalRecord({ ...newMedicalRecord, condition: e.target.value })}
                    placeholder="e.g., Diabetes"
                    size="sm"
                  />
                  <Input
                    label="Diagnosed Date"
                    type="date"
                    value={newMedicalRecord.date}
                    onChange={(e) => setNewMedicalRecord({ ...newMedicalRecord, date: e.target.value })}
                    size="sm"
                  />
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddMedicalRecord}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Record
                    </button>
                  </div>
                </div>

                <Input
                  value={newMedicalRecord.notes}
                  onChange={(e) => setNewMedicalRecord({ ...newMedicalRecord, notes: e.target.value })}
                  placeholder="Additional notes"
                  size="sm"
                  className="mb-4"
                />

                {/* Medical Records List */}
                {formData.medicalRecords.length > 0 && (
                  <div className="space-y-2">
                    {formData.medicalRecords.map((record, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{record.condition}</div>
                          <div className="text-sm text-gray-600">Diagnosed: {record.date}</div>
                          {record.notes && <div className="text-sm text-gray-500">{record.notes}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={() => dispatch(removeMedicalRecord(index))}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Medicine Intake */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Current Medications</h3>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <Input
                    label="Medicine Name"
                    value={newMedicine.name}
                    onChange={(e) => setNewMedicine({ ...newMedicine, name: e.target.value })}
                    placeholder="e.g., Aspirin"
                    size="sm"
                  />
                  <Input
                    label="Dosage"
                    value={newMedicine.dosage}
                    onChange={(e) => setNewMedicine({ ...newMedicine, dosage: e.target.value })}
                    placeholder="e.g., 100mg"
                    size="sm"
                  />
                  <Input
                    label="Frequency"
                    value={newMedicine.frequency}
                    onChange={(e) => setNewMedicine({ ...newMedicine, frequency: e.target.value })}
                    placeholder="e.g., Twice daily"
                    size="sm"
                  />
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddMedicine}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Medicine
                    </button>
                  </div>
                </div>

                {formData.medicineIntake.length > 0 && (
                  <div className="space-y-2">
                    {formData.medicineIntake.map((medicine, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{medicine.name}</div>
                          <div className="text-sm text-gray-600">
                            {medicine.dosage} • {medicine.frequency}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => dispatch(removeMedicineIntake(index))}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Health Records */}
              <div className="grid grid-cols-1 gap-6">
                <Textarea
                  label="General Health Records"
                  name="healthRecords"
                  value={formData.healthRecords}
                  onChange={(e) => handleFieldChange('healthRecords', e.target.value)}
                  placeholder="Enter any additional health information..."
                  rows={3}
                />

                <Textarea
                  label="Injury Records"
                  name="injuryRecords"
                  value={formData.injuryRecords}
                  onChange={(e) => handleFieldChange('injuryRecords', e.target.value)}
                  placeholder="Enter any injury history..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Family & Reference Tab */}
          {activeTab === 'family' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Family & Reference Information
              </h2>

              {/* Marital Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Marital Status"
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={(e) => handleFieldChange('maritalStatus', e.target.value)}
                  options={maritalStatus}
                />

                {formData.maritalStatus === 'married' && (
                  <Input
                    label="Number of Children"
                    name="numberOfChildren"
                    type="number"
                    value={formData.numberOfChildren}
                    onChange={(e) => handleFieldChange('numberOfChildren', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                )}
              </div>

              {/* Female Specific - Pregnancy */}
              {formData.gender === 'female' && (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-4">Pregnancy Information</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Select
                      label="Pregnancy Status"
                      name="pregnancyStatus"
                      value={formData.pregnancyStatus}
                      onChange={(e) => handleFieldChange('pregnancyStatus', e.target.value)}
                      options={pregnancyStatus}
                    />

                    {formData.pregnancyStatus === 'pregnant' && (
                      <Textarea
                        label="Pregnancy Details"
                        name="pregnancyDetails"
                        value={formData.pregnancyDetails}
                        onChange={(e) => handleFieldChange('pregnancyDetails', e.target.value)}
                        placeholder="Enter pregnancy-related information..."
                        rows={3}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Reference Information */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Reference Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Referred By"
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={(e) => handleFieldChange('referredBy', e.target.value)}
                    placeholder="Enter referrer name"
                    optional
                  />

                  <Input
                    label="Reference Name"
                    name="referenceName"
                    value={formData.referenceName}
                    onChange={(e) => handleFieldChange('referenceName', e.target.value)}
                    placeholder="Enter reference contact name"
                    optional
                  />

                  <Input
                    label="Reference Mobile"
                    name="referenceMobile"
                    type="tel"
                    value={formData.referenceMobile}
                    onChange={(e) => handleFieldChange('referenceMobile', e.target.value)}
                    placeholder="Enter reference mobile number"
                    icon={Phone}
                    optional
                  />

                  <Input
                    label="Reference Location"
                    name="referenceLocation"
                    value={formData.referenceLocation}
                    onChange={(e) => handleFieldChange('referenceLocation', e.target.value)}
                    placeholder="Enter reference location"
                    icon={MapPin}
                    optional
                  />
                </div>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-orange-600" />
                Subscription Plans
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {subscriptionDetails.map((plan) => (
                  <div
                    key={plan.id}
                    className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                    {plan.price && (
                      <div className="text-3xl font-bold text-blue-600 mb-4">₹{plan.price}</div>
                    )}
                    {plan.pricePerDay && (
                      <div className="text-sm text-gray-600 mb-4">₹{plan.pricePerDay}/day</div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-sm text-gray-500 italic">
                Note: Subscription will be activated after customer creation. You can manage subscriptions from the customer details page.
              </p>
            </div>
          )}

          {/* Account & Security Tab */}
          {activeTab === 'account' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Account & Security
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  placeholder="Enter password"
                  showPasswordToggle
                  helperText="Leave empty if you don't want to create login credentials"
                  optional
                />

                <Input
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                  placeholder="Confirm password"
                  showPasswordToggle
                  optional
                />
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-semibold text-gray-800">Verification Status</h3>
                    <p className="text-sm text-gray-600 mt-1">Verify customer identity before activation</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isVerified}
                      onChange={(e) => handleFieldChange('isVerified', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
              <p className="text-red-800 font-medium">Error: {error}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddCustomer;
